import { sessionStorageExpiring, sessionItems } from "./sessionStorage";
import type { UtmFields, UtmQueryParam } from "@/commonTypes/utm";

const UTMS_SESSION_STORAGE_KEY = "utms";
const UTM_CONTENT = "utm_content";

const UTM_PARAM_TO_PAYLOAD_KEY = {
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_content: "utmContent",
  utm_term: "utmTerm",
} as const satisfies Record<UtmQueryParam, keyof UtmFields>;

const UTM_PARAMS = Object.keys(UTM_PARAM_TO_PAYLOAD_KEY) as UtmQueryParam[];

export type SessionUtmsPayload = UtmFields;

// Reads experiment assignments from session storage and converts them to CSV.
const getExperimentAssignmentsString = (): string | undefined => {
  const assignments = sessionStorageExpiring.get<Record<string, string>>(
    sessionItems.EXPERIMENT_ASSIGNMENTS,
  );

  if (!assignments || Object.keys(assignments).length === 0) {
    return undefined;
  }

  return Object.entries(assignments)
    .map(([experimentId, variant]) => `${experimentId}:${variant}`)
    .join(",");
};

// Merges two comma-separated strings and removes duplicate values.
const mergeCsvValues = (currentValue: string, newValue: string): string => {
  const currentItems = currentValue
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  const newItems = newValue
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  return Array.from(new Set([...currentItems, ...newItems])).join(",");
};

// Returns normalized UTM values currently saved in session storage.
const readStoredUtms = (): Partial<Record<UtmQueryParam, string>> | null => {
  const rawUtms = sessionStorage.getItem(UTMS_SESSION_STORAGE_KEY);
  if (!rawUtms) return null;

  try {
    const parsedUtms = JSON.parse(rawUtms);
    if (!parsedUtms || typeof parsedUtms !== "object") {
      return null;
    }

    return UTM_PARAMS.reduce<Partial<Record<UtmQueryParam, string>>>(
      (acc, key) => {
        const value = parsedUtms[key];
        if (typeof value === "string" && value.trim()) {
          acc[key] = value.trim();
        }
        return acc;
      },
      {},
    );
  } catch {
    return null;
  }
};

// Persists UTM values to session storage as JSON.
const saveStoredUtms = (utms: Partial<Record<UtmQueryParam, string>>): void => {
  sessionStorage.setItem(UTMS_SESSION_STORAGE_KEY, JSON.stringify(utms));
};

// Updates stored UTM values from URL params and reports whether any were found.
const overrideSessionStorageWithParams = (params: URLSearchParams): boolean => {
  const currentUtms = readStoredUtms() ?? {};
  let nextUtms = { ...currentUtms };
  let foundUtm = false;

  UTM_PARAMS.forEach((param) => {
    const value = params.get(param)?.trim();
    if (!value) return;

    nextUtms = {
      ...nextUtms,
      [param]: value,
    };
    foundUtm = true;
  });

  if (foundUtm) {
    saveStoredUtms(nextUtms);
  }

  return foundUtm;
};

// Appends missing stored UTM params (and experiment content) to a URL.
export const appendSessionUtmsToUrl = (url: string): string => {
  if (typeof window === "undefined") return url;

  try {
    const parsedUrl = new URL(url, window.location.origin);
    const utms = readStoredUtms();

    if (utms) {
      UTM_PARAMS.forEach((param) => {
        if (parsedUrl.searchParams.has(param)) return;

        const value = utms[param];
        if (value) {
          parsedUrl.searchParams.set(param, value);
        }
      });
    }

    const experimentAssignments = getExperimentAssignmentsString();
    if (experimentAssignments) {
      const currentUtmContent = parsedUrl.searchParams.get(UTM_CONTENT);
      const mergedUtmContent = currentUtmContent
        ? mergeCsvValues(currentUtmContent, experimentAssignments)
        : experimentAssignments;

      if (mergedUtmContent) {
        parsedUrl.searchParams.set(UTM_CONTENT, mergedUtmContent);
      }
    }

    const isAbsoluteUrl = /^https?:\/\//i.test(url);
    if (isAbsoluteUrl) {
      return parsedUrl.toString();
    }

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (e) {
    console.warn("Could not append UTM parameters", e);
    return url;
  }
};

// Captures UTM params from the current URL once and stores them in session storage.
export const saveUtmQueryStringToSessionStorage: () => void = () => {
  if (typeof window == "undefined") return;

  try {
    const urlParams = new URLSearchParams(window.location.search);
    overrideSessionStorageWithParams(urlParams);
  } catch (e) {
    console.warn("Error saving UTM parameters to session storage", e);
  }
};

// Builds the API payload shape from stored session UTM values.
export const getSessionUtmsPayload = (): SessionUtmsPayload | null => {
  if (typeof window === "undefined") return null;

  const utms = readStoredUtms();
  if (!utms) return null;

  const payload = UTM_PARAMS.reduce<SessionUtmsPayload>((acc, param) => {
    const payloadKey = UTM_PARAM_TO_PAYLOAD_KEY[param];
    const value = utms[param];
    if (value) {
      acc[payloadKey] = value;
    }
    return acc;
  }, {});

  const hasAnyUtm = Object.values(payload).some((value) => Boolean(value));
  return hasAnyUtm ? payload : null;
};
